import React, { useState, useEffect } from 'react'
import {
  SparklesIcon,
  ChartBarIcon,
  LightBulbIcon,
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  TagIcon
} from '@heroicons/react/24/outline'
import { aiAPI } from '@/services/api'

interface FinancialHealthScore {
  overall_score: number
  components: {
    cash_flow_stability: number
    expense_control: number
    revenue_diversification: number
    emergency_reserve: number
    growth_trend: number
  }
  recommendations: string[]
  risk_level: 'Low' | 'Medium' | 'High'
  analysis_date: string
  ai_provider?: string
}

const AIInsights: React.FC = () => {
  const [healthScore, setHealthScore] = useState<FinancialHealthScore | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchHealthScore = async () => {
      try {
        setLoading(true)
        const data = await aiAPI.getFinancialHealth()
        setHealthScore(data)
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar análise de IA')
      } finally {
        setLoading(false)
      }
    }

    fetchHealthScore()
  }, [])

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getScoreBackground = (score: number) => {
    if (score >= 80) return 'bg-green-100'
    if (score >= 60) return 'bg-yellow-100'
    return 'bg-red-100'
  }

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'Low': return 'bg-green-100 text-green-800'
      case 'Medium': return 'bg-yellow-100 text-yellow-800'
      case 'High': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getProviderBadge = (provider?: string) => {
    console.log('🎯 [AIInsights] getProviderBadge CHAMADA:', {
      'provider recebido': provider,
      'provider é undefined?': provider === undefined,
      'provider é null?': provider === null,
      'typeof provider': typeof provider
    })

    if (!provider) {
      console.log('❌ [AIInsights] getProviderBadge retornou NULL (provider vazio)')
      return null
    }

    const badges: Record<string, { color: string; label: string; icon: string }> = {
      'gemini': { color: 'bg-blue-500 text-white border-2 border-blue-600 shadow-lg', label: 'Gemini AI', icon: '🔵' },
      'claude': { color: 'bg-purple-500 text-white border-2 border-purple-600 shadow-lg', label: 'Claude AI', icon: '🟣' },
      'openai': { color: 'bg-green-500 text-white border-2 border-green-600 shadow-lg', label: 'GPT AI', icon: '🟢' },
      'rules': { color: 'bg-orange-500 text-white border-2 border-orange-600 shadow-lg', label: 'Sistema de Regras', icon: '⚙️' }
    }
    const badge = badges[provider.toLowerCase()] || badges['rules']

    console.log('✅ [AIInsights] getProviderBadge vai retornar badge:', {
      'provider normalizado': provider.toLowerCase(),
      'badge selecionado': badge.label,
      'classe CSS': badge.color
    })

    return (
      <span className={`inline-flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-lg ${badge.color}`}>
        <span className="text-lg">{badge.icon}</span>
        <span>{badge.label}</span>
      </span>
    )
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-32 bg-gray-100 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <h3 className="text-red-800 font-semibold mb-2">Erro na análise de IA</h3>
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <SparklesIcon className="w-7 h-7 text-purple-600" />
            Inteligência Artificial
          </h1>
          <p className="text-gray-600">Análises e insights financeiros com IA</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <ClockIcon className="w-4 h-4" />
          Atualizado: {healthScore ? new Date(healthScore.analysis_date).toLocaleDateString('pt-BR') : ''}
        </div>
      </div>

      {/* Financial Health Score */}
      {healthScore && (
        <>
          {/* Overall Score Card */}
          <div className="bg-gradient-to-r from-purple-500 to-blue-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold mb-2">Score de Saúde Financeira</h2>
                <div className="flex items-center gap-4">
                  <span className="text-4xl font-bold">{healthScore.overall_score}</span>
                  <div>
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getRiskColor(healthScore.risk_level)}`}>
                      Risco: {healthScore.risk_level === 'Low' ? 'Baixo' : healthScore.risk_level === 'Medium' ? 'Médio' : 'Alto'}
                    </span>
                  </div>
                </div>
              </div>
              <ChartBarIcon className="w-16 h-16 text-white opacity-20" />
            </div>
          </div>

          {/* Data Source Indicator */}
          <div className="p-4 bg-green-100 border-2 border-green-400 rounded-xl shadow-sm">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-2">
              <span className="text-green-700 font-bold text-base">✓ ANÁLISE COM DADOS REAIS DO BANCO DE DADOS</span>
              <span className="text-green-600 text-sm">
                (Scores calculados com transações, contas e categorias reais do seu banco de dados)
              </span>
            </div>
          </div>

          {/* Score Components */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(healthScore.components).map(([key, score]) => {
              const labels: Record<string, string> = {
                cash_flow_stability: 'Estabilidade do Fluxo',
                expense_control: 'Controle de Despesas',
                revenue_diversification: 'Diversificação',
                emergency_reserve: 'Reserva de Emergência',
                growth_trend: 'Tendência de Crescimento'
              }

              const icons: Record<string, JSX.Element> = {
                cash_flow_stability: <ArrowTrendingUpIcon className="w-6 h-6" />,
                expense_control: <ChartBarIcon className="w-6 h-6" />,
                revenue_diversification: <SparklesIcon className="w-6 h-6" />,
                emergency_reserve: <CheckCircleIcon className="w-6 h-6" />,
                growth_trend: <ArrowTrendingUpIcon className="w-6 h-6" />
              }

              return (
                <div key={key} className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-2 rounded-lg ${getScoreBackground(score)}`}>
                      <div className={getScoreColor(score)}>
                        {icons[key]}
                      </div>
                    </div>
                    <span className={`text-2xl font-bold ${getScoreColor(score)}`}>
                      {score}
                    </span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{labels[key]}</h3>

                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        score >= 80 ? 'bg-green-500' :
                        score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${score}%` }}
                    ></div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Recommendations */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <LightBulbIcon className="w-5 h-5 text-yellow-500" />
                Recomendações da IA
              </h3>
              {getProviderBadge(healthScore.ai_provider)}
            </div>

            {healthScore.recommendations.length > 0 ? (
              <div className="space-y-3">
                {healthScore.recommendations.map((recommendation, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                    <ExclamationTriangleIcon className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-blue-800">{recommendation}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">Parabéns! Sua saúde financeira está excelente. Continue assim!</p>
            )}
          </div>

          {/* AI Tools Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <TagIcon className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-semibold text-gray-900">Categorização Automática</h3>
              </div>
              <p className="text-gray-600 mb-4">Categorize suas transações automaticamente com IA</p>
              <button className="text-purple-600 font-medium hover:text-purple-700">
                Configurar →
              </button>
            </div>

            <div
              onClick={() => window.location.href = '/ai/predictions'}
              className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <ArrowTrendingUpIcon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900">Predições</h3>
              </div>
              <p className="text-gray-600 mb-4">Previsões de fluxo de caixa e tendências</p>
              <button className="text-blue-600 font-medium hover:text-blue-700">
                Ver Predições →
              </button>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-green-100 rounded-lg">
                  <LightBulbIcon className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-gray-900">Insights Avançados</h3>
              </div>
              <p className="text-gray-600 mb-4">Análises detalhadas e insights personalizados</p>
              <button className="text-green-600 font-medium hover:text-green-700">
                Explorar →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default AIInsights